'use client';

import { Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import {
    UserPlus,
    Handshake,
    FileText,
    Video,
    UserCheck,
    ArrowRight,
    ShieldCheck,
    Target,
    Zap,
    AlertCircle,
    CheckCircle2,
    Users
} from 'lucide-react';
import { GlobalBrandedLoader } from '@/doi-tac-module/components/global-branded-loader';
import React, { useState, useEffect } from 'react';
import { validateAgencyCode } from '@/doi-tac-module/actions/agency-actions';
import { agencyTranslations as at } from '@/doi-tac-module/constants/agency-translations';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Image from 'next/image';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { cn } from '@/doi-tac-module/lib/utils';
const interviewTrainingTranslations: any = {
    hero: {
        titlePrefix: { vi: "Kỹ năng phỏng vấn", en: "Interview Skills" },
        titleSuffix: { vi: "Tokutei Gino", en: "Tokutei Gino" },
        description: { 
            vi: "Hệ thống AI thông minh giúp bạn chuẩn bị nội dung, luyện phát âm và tự tin vượt qua mọi cuộc phỏng vấn Tokutei.",
            en: "Smart AI system helps you prepare content, practice pronunciation, and confidently pass any Tokutei interview."
        },
        descriptionDesktop: {
            vi: "Hệ thống AI thông minh giúp bạn chuẩn bị nội dung, luyện phát âm và tự tin vượt qua mọi cuộc phỏng vấn Tokutei. Chinh phục nhà tuyển dụng Nhật Bản dễ dàng hơn bao giờ hết.",
            en: "Smart AI system helps you prepare content, practice pronunciation, and confidently pass any Tokutei interview. Conquering Japanese employers is easier than ever."
        },
        ctaRegister: { vi: "Bắt đầu luyện tập ngay", en: "Start practicing now" },
        ctaProcess: { vi: "Tìm hiểu lộ trình", en: "Explore roadmap" }
    },
    painPoints: {
        item1: {
            title: { vi: "Rào cản ngôn ngữ", en: "Language Barrier" },
            description: { 
                vi: "Lo lắng vì không biết cách diễn đạt ý tưởng bằng tiếng Nhật một cách tự nhiên và chuyên nghiệp.",
                en: "Worried about not knowing how to express ideas in Japanese naturally and professionally."
            }
        },
        item2: {
            title: { vi: "Phát âm chưa chuẩn", en: "Pronunciation Issues" },
            description: { 
                vi: "Khó khăn trong việc luyện luyến láy và ngữ điệu chuẩn như người bản xứ khi trả lời câu hỏi.",
                en: "Difficulty in practicing intonation and accent like a native speaker when answering questions."
            }
        },
        item3: {
            title: { vi: "Thiếu chuẩn bị", en: "Lack of Preparation" },
            description: { 
                vi: "Không biết nhà tuyển dụng Nhật Bản thường hỏi gì và cách trả lời sao cho ấn tượng nhất.",
                en: "Don't know what Japanese employers usually ask and how to answer most impressively."
            }
        }
    },
    solutions: {
        title: { vi: "Luyện tập phỏng vấn", en: "Interview Practice" },
        subtitle: { vi: "Đột phá cùng công nghệ AI", en: "Breakthrough with AI Tech" },
        items: [
            { vi: "Tự động dịch và tối ưu câu trả lời sang tiếng Nhật chuyên nghiệp.", en: "Auto-translate and optimize answers into professional Japanese." },
            { vi: "AI tạo file nghe chuẩn bản xứ để luyện phát âm và luyến láy hàng ngày.", en: "AI generates native audio for daily pronunciation and intonation practice." },
            { vi: "Lưu trữ và quản lý kho câu hỏi phỏng vấn cá nhân hóa lâu dài.", en: "Long-term storage and management of personalized interview questions." }
        ]
    },
    process: {
        title: { vi: "Lộ trình 5 bước thành công", en: "5-Step Roadmap to Success" },
        description: { 
            vi: "Quy trình luyện tập khoa học giúp bạn tự tin làm chủ buổi phỏng vấn Tokutei.",
            en: "Scientific practice process helps you confidently master the Tokutei interview."
        },
        ctaAction: { vi: "Tạo tài khoản ngay", en: "Create account now" },
        steps: [
            {
                stageLabel: { vi: "Bước 1", en: "Step 1" },
                title: { vi: "Tạo tài khoản quản lý", en: "Create Management Account" },
                description: { 
                    vi: "Tạo tài khoản để quản lý và lưu trữ lâu dài nội dung phỏng vấn của bạn trên hệ thống.",
                    en: "Create an account to manage and store your interview content on the system long-term."
                }
            },
            {
                stageLabel: { vi: "Bước 2", en: "Step 2" },
                title: { vi: "Soạn nội dung tiếng Việt", en: "Prepare Vietnamese Content" },
                description: { 
                    vi: "Điền nội dung trả lời các câu hỏi hay bị hỏi khi phỏng vấn Tokutei bằng tiếng Việt, AI sẽ hỗ trợ bạn dịch thuật.",
                    en: "Fill in answers to common Tokutei interview questions in Vietnamese; AI will assist with translation."
                }
            },
            {
                stageLabel: { vi: "Bước 3", en: "Step 3" },
                title: { vi: "Kiểm tra kết quả AI", en: "Check AI Results" },
                description: { 
                    vi: "Kiểm tra nội dung câu trả lời và nghe cách phát âm tiếng Nhật được AI tạo ra một cách chuẩn xác.",
                    en: "Check the answer content and listen to the Japanese pronunciation accurately generated by AI."
                }
            },
            {
                stageLabel: { vi: "Bước 4", en: "Step 4" },
                title: { vi: "Tối ưu hóa nội dung", en: "Optimize Content" },
                description: { 
                    vi: "Chỉnh sửa lại các câu trả lời chưa đạt hoặc chưa ưng ý để đảm bảo chất lượng phỏng vấn tốt nhất.",
                    en: "Re-edit answers that are not yet satisfactory to ensure the best interview quality."
                }
            },
            {
                stageLabel: { vi: "Bước 5", en: "Step 5" },
                title: { vi: "Luyện tập hàng ngày", en: "Daily Practice" },
                description: { 
                    vi: "Hàng ngày vào bật đi bật lại, học cách phát âm và luyến láy của người Nhật để nâng cao năng lực và sự tự tin.",
                    en: "Listen repeatedly every day, learn Japanese pronunciation and intonation to enhance ability and confidence."
                }
            }
        ]
    },
    finalCta: {
        title: { vi: "Bạn đã sẵn sàng chinh phục Tokutei?", en: "Ready to Conquer Tokutei?" },
        description: { 
            vi: "Hãy bắt đầu lộ trình luyện tập ngay hôm nay để không bỏ lỡ cơ hội làm việc tại Nhật Bản.",
            en: "Start your practice roadmap today so you don't miss out on working opportunities in Japan."
        },
        trustLabel: { vi: "ĐƯỢC TIN TƯỞNG BỞI CỘNG ĐỒNG TOKUTEI", en: "TRUSTED BY TOKUTEI COMMUNITY" }
    }
};
const stepsData = [
    {
        id: 1,
        icon: UserPlus,
        color: "bg-[#19A6DF]/10 text-[#19A6DF]",
        image: "/img/PHONGVAN/phongvan01.jpg",
        dataAiHint: "modern registration form on smartphone"
    },
    {
        id: 2,
        icon: Handshake,
        color: "bg-[#F2B92A]/10 text-[#F2B92A]",
        image: "/img/PHONGVAN/phongvan02.jpg",
        dataAiHint: "business handshake partners"
    },
    {
        id: 3,
        icon: FileText,
        color: "bg-[#AFC536]/10 text-[#AFC536]",
        image: "/img/PHONGVAN/phongvan03.jpg",
        dataAiHint: "professional resume on laptop screen"
    },
    {
        id: 4,
        icon: Video,
        color: "bg-[#0D8DC8]/10 text-[#0D8DC8]",
        image: "/img/PHONGVAN/phongvan04.jpg",
        dataAiHint: "man recording video in workshop"
    },
    {
        id: 5,
        icon: UserCheck,
        color: "bg-[#19A6DF]/10 text-[#19A6DF]",
        image: "/img/PHONGVAN/phongvan05.jpg",
        dataAiHint: "successful recruitment interview"
    }
];

const painPointsData = [
    { icon: AlertCircle, key: 'item1' },
    { icon: Zap, key: 'item2' },
    { icon: Target, key: 'item3' }
];

function RoadmapContent() {
    const { language } = useLanguage();
    const router = useRouter();
    const searchParams = useSearchParams();
    const refId = searchParams.get('ref');
    
    const { user, isLoggedIn } = useAuth();
    const t = interviewTrainingTranslations;

    const [isValidatingRef, setIsValidatingRef] = useState(!!refId);
    const [showInvalidRefPopup, setShowInvalidRefPopup] = useState(false);

    // Redirect về trang tạo nội dung phỏng vấn
    const redirectTarget = encodeURIComponent('/tao-ho-so/tao-noi-dung-phong-van');
    const authUrl = refId
        ? `/xac-thuc?ref=${refId}&redirect=${redirectTarget}`
        : `/xac-thuc?redirect=${redirectTarget}`;

    let mainBtnText = t.hero.ctaRegister[language];
    let mainBtnLink = authUrl;
    let heroDescription: React.ReactNode = t.hero.descriptionDesktop?.[language] || t.hero.description[language];

    if (isLoggedIn) {
        mainBtnText = "Vào luyện tập ngay";
        mainBtnLink = "/tao-ho-so/tao-noi-dung-phong-van";
    }

    // Validate referral code on mount
    useEffect(() => {
        const checkRef = async () => {
            if (!refId) return;
            
            setIsValidatingRef(true);
            try {
                const res = await validateAgencyCode(refId);
                if (!res.success) {
                    setShowInvalidRefPopup(true);
                }
            } catch (err) {
                console.error("Failed to validate ref code on roadmap:", err);
                setShowInvalidRefPopup(true);
            } finally {
                setIsValidatingRef(false);
            }
        };
        
        checkRef();
    }, [refId]);

    return (
        <div className="flex flex-col w-full min-h-screen bg-slate-50/50">
            {/* Hero Section */}
            <section className="relative w-full pt-20 md:pt-28 pb-16 bg-blue-600 bg-gradient-to-r from-blue-600 to-sky-500 overflow-hidden">
                <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,rgba(255,255,255,0),rgba(255,255,255,0.5))]" />
                <div className="container relative mx-auto px-4 md:px-6 text-center text-white">
                    <h1 className="text-4xl md:text-5xl font-headline font-bold mb-8 leading-snug md:leading-tight">
                        {t.hero.titlePrefix[language]} <br /> <span className="text-[#F2B92A]">{t.hero.titleSuffix[language]}</span>
                    </h1>
                    {/* Mobile description */}
                    <p className="md:hidden text-base text-white/90 max-w-3xl mx-auto mb-10 leading-relaxed">
                        {heroDescription}
                    </p>
                    {/* Desktop description (3 lines) */}
                    <p className="hidden md:block text-lg text-white/90 max-w-3xl mx-auto mb-10 leading-relaxed">
                        {heroDescription}
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Button size="lg" className="bg-[#F2B92A] hover:bg-[#F2B92A]/90 text-white font-medium h-12 px-8 text-lg rounded-xl shadow-lg shadow-orange-950/20" asChild>
                            <Link href={mainBtnLink}>{mainBtnText}</Link>
                        </Button>
                        <Button variant="outline" size="lg" className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm h-12 px-8 text-lg rounded-xl font-medium" asChild>
                            <Link href="#process">{t.hero.ctaProcess[language]}</Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* Pain Points Section */}
            <section className="w-full py-20 -mt-10 relative z-10">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {painPointsData.map((point, index) => {
                            const content = (t.painPoints as any)[point.key];
                            return (
                                <Card key={index} className="border-none shadow-xl shadow-slate-200/50 rounded-2xl overflow-hidden group hover:scale-[1.02] transition-transform duration-300">
                                    <CardContent className="p-8">
                                        <div className="h-14 w-14 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center mb-6 group-hover:bg-red-500 group-hover:text-white transition-colors duration-300">
                                            <point.icon className="h-7 w-7" />
                                        </div>
                                        <h3 className="text-2xl font-headline font-bold mb-4 text-slate-800">
                                            {content.title[language]}
                                        </h3>
                                        <p className="text-slate-600 leading-relaxed">
                                            {content.description[language]}
                                        </p>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CVP Section */}
            <section className="w-full py-20 bg-white border-y">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="flex flex-col-reverse lg:flex-row items-center gap-12 lg:gap-20">
                        <div className="lg:w-1/2 w-full">
                            <div className="relative aspect-[3/2] rounded-3xl overflow-hidden shadow-2xl bg-white">
                                <Image
                                    src="/img/BANGLADESH/TRANGVIETNAM/lotrinh0.jpg"
                                    alt="Interview Practice"
                                    fill
                                    sizes="(max-width: 768px) 100vw, 50vw"
                                    className="object-contain"
                                />
                            </div>
                        </div>
                        <div className="lg:w-1/2">
                            <h2 className="text-3xl md:text-4xl font-headline font-bold mb-8 text-slate-900 leading-tight">
                                {t.solutions.title[language]} <br /> <span className="text-[#19A6DF]">{t.solutions.subtitle[language]}</span>
                            </h2>
                            <div className="space-y-6">
                                {t.solutions.items.map((item: any, i: number) => (
                                    <div key={i} className="flex items-start gap-4">
                                        <div className="mt-1 h-6 w-6 rounded-full bg-green-50 text-green-600 flex flex-shrink-0 items-center justify-center">
                                            <CheckCircle2 className="h-4 w-4" />
                                        </div>
                                        <p className="text-lg text-slate-700">{item[language]}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Detailed 5-Step Process */}
            <section id="process" className="w-full py-32 bg-background">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="max-w-3xl mx-auto text-center mb-24">
                        <h2 className="text-3xl md:text-4xl font-headline font-bold mb-6 text-slate-900">
                            {t.process.title[language]}
                        </h2>
                        <p className="text-xl text-slate-500">
                            {t.process.description[language]}
                        </p>
                    </div>

                    <div className="space-y-32">
                        {stepsData.map((step, index) => {
                            const stepContent = t.process.steps[index];
                            return (
                                <div key={step.id} className={`flex flex-col-reverse lg:flex-row items-center gap-12 lg:gap-24 ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
                                    <div className="lg:w-1/2 w-full">
                                        <div className="relative group">
                                            <div className={`absolute -inset-4 rounded-[2rem] ${step.color} blur-2xl opacity-20 group-hover:opacity-40 transition-opacity`} />
                                            <div className="relative aspect-[3/2] rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white bg-white">
                                                <Image
                                                    src={step.image}
                                                    alt={stepContent.title[language]}
                                                    fill
                                                    sizes="(max-width: 768px) 100vw, 50vw"
                                                    priority={index === 0}
                                                    className="object-contain transition-transform duration-500 group-hover:scale-105 will-change-transform"
                                                    data-ai-hint={step.dataAiHint}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="lg:w-1/2">
                                        <div className={`inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-xl text-sm font-bold tracking-wider ${step.color}`}>
                                            <step.icon className="h-5 w-5" />
                                            {stepContent.stageLabel[language]}
                                        </div>
                                        <h3 className="text-2xl md:text-3xl font-headline font-bold mb-6 text-slate-900 leading-tight flex items-center flex-wrap gap-3">
                                            {stepContent.title[language]}
                                            {isLoggedIn && step.id === 1 && (
                                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 gap-1.5 py-1 text-sm rounded-xl">
                                                    <CheckCircle2 className="w-4 h-4 text-green-500" /> Đã hoàn thành
                                                </Badge>
                                            )}
                                        </h3>
                                        <p className="text-xl text-slate-600 leading-relaxed mb-10">
                                            {stepContent.description[language]}
                                        </p>
                                        <div className="flex flex-col sm:flex-row gap-4">
                                            <Button className="bg-[#F2B92A] hover:bg-[#F2B92A]/90 text-white font-medium h-12 px-8 text-lg rounded-xl shadow-lg shadow-orange-950/10 group" asChild>
                                                <Link href={mainBtnLink}>
                                                    {isLoggedIn ? "Tiếp tục lộ trình" : t.process.ctaAction[language]}
                                                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                                                </Link>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="w-full py-24 bg-slate-950 text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#19A6DF]/20 rounded-full blur-[120px] -mr-64 -mt-64" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#F2B92A]/10 rounded-full blur-[120px] -ml-64 -mb-64" />

                <div className="container relative mx-auto px-4 md:px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-headline font-bold mb-8">
                        {t.finalCta.title[language]}
                    </h2>
                    <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-12">
                        {t.finalCta.description[language]}
                    </p>
                    <div className="flex justify-center gap-6">
                        <Button size="lg" className="bg-[#19A6DF] hover:bg-[#19A6DF]/90 text-white font-medium h-12 px-10 text-lg rounded-2xl shadow-xl shadow-blue-900/40" asChild>
                            <Link href={mainBtnLink}>{mainBtnText}</Link>
                        </Button>
                    </div>

                    <div className="mt-16 flex items-center justify-center gap-8 grayscale opacity-50">
                        <ShieldCheck className="h-10 w-10" />
                        <span className="text-sm font-medium tracking-[0.2em]">
                            {t.finalCta.trustLabel[language]}
                        </span>
                    </div>
                </div>
            </section>

            {/* Popup thông báo mã giới thiệu không hợp lệ */}
            <AlertDialog open={showInvalidRefPopup}>
                <AlertDialogContent className="max-w-md rounded-3xl">
                    <AlertDialogHeader>
                        <div className="mx-auto bg-red-100 rounded-full p-3 w-fit mb-4">
                            <ShieldCheck className="h-8 w-8 text-red-600" />
                        </div>
                        <AlertDialogTitle className="text-2xl font-bold text-center font-headline">
                            {at.sso.invalidRef.title[language]}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-center text-slate-600 text-lg">
                            {at.sso.invalidRef.desc[language]}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col sm:flex-col gap-2 mt-6">
                        <AlertDialogAction 
                            onClick={() => router.push('/')}
                            className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl text-base font-semibold"
                        >
                            {at.sso.invalidRef.button[language]}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Loading Overlay khi đang validate mã ref */}
            {isValidatingRef && <GlobalBrandedLoader forceShow={true} />}
        </div>
    );
}

export default function QuyTrinhPage() {
    return (
        <Suspense fallback={<GlobalBrandedLoader forceShow={true} />}>
            <RoadmapContent />
        </Suspense>
    );
}

